
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock3, RotateCcw, Settings, Flag, Handshake, SkipBack, SkipForward, Play, Loader2 } from 'lucide-react';
import { 
  Brain, FlipHorizontal, ChevronLeft, ChevronRight, RotateCw
} from 'lucide-react';
import { toast } from 'sonner';

import { 
  GameState, 
  Position, 
  createInitialGameState, 
  validateMove, 
  getGameStatus,
  getAllLegalMoves,
  positionToAlgebraic,
  algebraicToPosition
} from '@/lib/chess-engine';
import { getBotMove, getAvailableBotElos, getBotPersonality } from '@/lib/bot-ai';
import { parseTimeControl, formatTime } from '@/lib/clock-utils';
import { useLocalClock } from '@/hooks/useLocalClock';
import { NativeChessBoard } from './NativeChessBoard';
import { getFenFromGameState } from '@/lib/opening-book';
import { getBestMoveFromStockfish, clampStockfishElo, STOCKFISH_UCI_ELO_MIN, STOCKFISH_UCI_ELO_MAX, prewarmStockfish, getStockfishSource } from '@/lib/stockfish-engine';

interface ChessEngineConfig {
  botElo: number;
  playerColor: 'white' | 'black';
  timeControl: string;
  engine: 'native' | 'stockfish';
  allowFallback: boolean;
}

export function ChessEngineWidget() {
  const [gameState, setGameState] = useState<GameState>(createInitialGameState());
  const [gameStateHistory, setGameStateHistory] = useState<GameState[]>([createInitialGameState()]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [highlightedSquares, setHighlightedSquares] = useState<Position[]>([]);
  const [config, setConfig] = useState<ChessEngineConfig>({
    botElo: 800,
    playerColor: 'white',
    timeControl: '10+0',
    engine: 'stockfish',
    allowFallback: true
  });
  const [isFlipped, setIsFlipped] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [gameStatus, setGameStatus] = useState<string>('Game in progress');
  const [moveHistory, setMoveHistory] = useState<string[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [isGameResigned, setIsGameResigned] = useState(false);
  const [isDrawOffered, setIsDrawOffered] = useState(false);
  const [clockResetTrigger, setClockResetTrigger] = useState(0);
  const [stockfishStatus, setStockfishStatus] = useState<{
    isReady: boolean;
    source?: string;
    engineId?: string;
    lastError?: string;
  }>({ isReady: false });
  const [isTesting, setIsTesting] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const isPlayerTurn = useRef(true);
  
  // Get current game status for clock
  const currentGameStatus = (() => {
    const status = getGameStatus(gameState);
    return status.status === 'ongoing' || status.status === 'check' ? 'ongoing' : 'ended';
  })();
  
  // Initialize clock
  const { whiteTimeMs, blackTimeMs, isTimeUp, activePlayer } = useLocalClock(
    config.timeControl, 
    gameState.sideToMove, 
    currentGameStatus,
    clockResetTrigger
  );

  useEffect(() => {
    prewarmStockfish();
  }, []);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => {
      const newLogs = [...prev, `[${timestamp}] ${message}`];
      return newLogs.slice(-50); // Keep last 50 messages
    });
    
    setTimeout(() => {
      if (logRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    }, 0);
  };
  
  // Stockfish status tracking
  useEffect(() => {
    const checkStockfishStatus = async () => {
      try {
        const src = getStockfishSource();
        if (src) {
          setStockfishStatus(prev => ({
            ...prev,
            isReady: true,
            source: src
          }));
          addLog(`[Stockfish] Ready - source: ${src}`);
        }
      } catch (error) {
        setStockfishStatus(prev => ({
          ...prev,
          isReady: false,
          lastError: error instanceof Error ? error.message : 'Unknown error'
        }));
        addLog(`[Stockfish] Error: ${error}`);
      }
    };

    const timer = setTimeout(checkStockfishStatus, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Sanity test function
  const runSanityTest = async () => {
    if (isTesting) return;
    
    setIsTesting(true);
    addLog('[Test] Starting Stockfish sanity test...');
    
    try {
      const testFen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
      const startTime = Date.now();
      
      addLog('[Test] Testing with high Elo (3190) for 500ms...');
      const testMove = await getBestMoveFromStockfish(testFen, { 
        elo: 3190, 
        movetime: 500 
      });
      
      const duration = Date.now() - startTime;
      addLog(`[Test] Completed in ${duration}ms - Move: ${testMove}`);
      
      if (testMove && testMove !== '(none)') {
        addLog('[Test] ✓ Stockfish is responding correctly');
        toast.success('Stockfish test passed!');
        setStockfishStatus(prev => ({
          ...prev,
          isReady: true,
          lastError: undefined
        }));
      } else {
        addLog('[Test] ✗ Stockfish returned invalid move');
        toast.error('Stockfish test failed - invalid response');
        setStockfishStatus(prev => ({
          ...prev,
          isReady: false,
          lastError: 'Invalid response in test'
        }));
      }
    } catch (error) {
      addLog(`[Test] ✗ Stockfish test failed: ${error}`);
      toast.error('Stockfish test failed');
      setStockfishStatus(prev => ({
        ...prev,
        isReady: false,
        lastError: error instanceof Error ? error.message : 'Test failed'
      }));
    } finally {
      setIsTesting(false);
    }
  };
  
  useEffect(() => {
    const t = setTimeout(() => {
      const src = getStockfishSource();
      if (src) {
        addLog(`[Stockfish] loader=${src}`);
        setStockfishStatus(prev => ({ ...prev, source: src }));
      }
    }, 800);
    return () => clearTimeout(t);
  }, []);
  
  const updateGameStatus = () => {
    const status = getGameStatus(gameState);
    let statusText = '';
    
    switch (status.status) {
      case 'ongoing':
        statusText = `${gameState.sideToMove === 'white' ? 'White' : 'Black'} to move`;
        break;
      case 'check':
        statusText = `${gameState.sideToMove === 'white' ? 'White' : 'Black'} is in check`;
        break;
      case 'checkmate':
        statusText = `Checkmate! ${status.winner === 'white' ? 'White' : 'Black'} wins`;
        break;
      case 'stalemate':
        statusText = 'Stalemate - Draw';
        break;
      case 'draw':
        statusText = `Draw - ${status.reason}`;
        break;
    }
    
    setGameStatus(statusText);
    return status;
  };

  const makeMove = async (from: Position, to: Position) => {
    const moveResult = validateMove(gameState, from, to);
    
    if (!moveResult.isLegal || !moveResult.newState) {
      addLog(`Illegal move: ${positionToAlgebraic(from)} -> ${positionToAlgebraic(to)}`);
      return false;
    }

    const moveNotation = `${positionToAlgebraic(from)}-${positionToAlgebraic(to)}`;
    
    // Update game state and history
    setGameState(moveResult.newState);
    setGameStateHistory(prev => {
      // Remove any moves after current index (for when replaying and making new moves)
      const newHistory = prev.slice(0, currentMoveIndex + 1);
      return [...newHistory, moveResult.newState];
    });
    setCurrentMoveIndex(prev => prev + 1);
    setMoveHistory(prev => [...prev, moveNotation]);
    addLog(`Move: ${moveNotation} (${gameState.sideToMove})`);
    
    // Update isPlayerTurn based on the new game state
    const isNowPlayerTurn = (config.playerColor === 'white' && moveResult.newState.sideToMove === 'white') ||
                            (config.playerColor === 'black' && moveResult.newState.sideToMove === 'black');
    isPlayerTurn.current = isNowPlayerTurn;
    
    setSelectedSquare(null);
    setHighlightedSquares([]);
    
    return true;
  };

  const handleSquareClick = async (position: Position) => {
    if (isThinking) return;
    
    const [row, col] = position;
    const clickedPiece = gameState.board[row][col];
    
    if (!selectedSquare) {
      // Select a piece
      if (clickedPiece && clickedPiece.color === gameState.sideToMove) {
        // Only allow player to move their own pieces
        if ((config.playerColor === 'white' && gameState.sideToMove === 'white') ||
            (config.playerColor === 'black' && gameState.sideToMove === 'black')) {
          setSelectedSquare(position);
          
          // Highlight possible moves
          const legalMoves = getAllLegalMoves(gameState)
            .filter(move => move.from[0] === row && move.from[1] === col)
            .map(move => move.to);
          setHighlightedSquares(legalMoves);
          
          addLog(`Selected ${clickedPiece.color} ${clickedPiece.type} at ${positionToAlgebraic(position)}`);
        }
      }
    } else {
      // Try to make a move
      const success = await makeMove(selectedSquare, position);
      // isPlayerTurn is now updated inside makeMove
    }
  };

  const requestBotMove = async () => {
    if (isThinking || gameState.sideToMove !== (config.playerColor === 'white' ? 'black' : 'white')) {
      return;
    }
    
    setIsThinking(true);
    const botPersonality = getBotPersonality(config.botElo);
    const engineName = config.engine === 'stockfish' ? 'Stockfish' : (botPersonality?.name || 'Bot');
    addLog(`${engineName} (${config.botElo} ELO) is thinking...`);
    
    try {
      let botMove: { from: Position; to: Position; promotion?: any } | null = null;

      if (config.engine === 'stockfish') {
        const fen = getFenFromGameState(gameState);
        const { timeMs } = parseTimeControl(config.timeControl);
        const movetime = Math.max(800, timeMs <= 60_000 ? 800 : timeMs <= 180_000 ? 900 : 1200);
        const uciElo = clampStockfishElo(config.botElo);
        addLog(`[Stockfish] FEN: ${fen}`);
        addLog(`[Stockfish] movetime=${movetime}ms, UCI_Elo=${uciElo}`);
        const uci = await getBestMoveFromStockfish(fen, { elo: uciElo, movetime });
        addLog(`[Stockfish] bestmove: ${uci}`);
        if (uci === '(none)') {
          addLog('Stockfish reports no legal moves (game over).');
          updateGameStatus();
          botMove = null;
        } else if (uci && uci.length >= 4) {
          const from = algebraicToPosition(uci.slice(0, 2));
          const to = algebraicToPosition(uci.slice(2, 4));
          const promoChar = uci.length >= 5 ? uci[4] : undefined;
          const promotion = promoChar ? (promoChar.toUpperCase() as any) : undefined;
          botMove = { from, to, promotion };
        }
      } else {
        addLog(`[Native] Using calibrated bot: ${engineName} (${config.botElo})`);
        botMove = await getBotMove(gameState, config.botElo);
      }
      
      if (botMove) {
        const moveResult = validateMove(gameState, botMove.from, botMove.to, botMove.promotion);
        
        if (moveResult.isLegal && moveResult.newState) {
          const moveNotation = `${positionToAlgebraic(botMove.from)}-${positionToAlgebraic(botMove.to)}`;
          // Update game state and history
          setGameState(moveResult.newState);
          setGameStateHistory(prev => {
            const newHistory = prev.slice(0, currentMoveIndex + 1);
            return [...newHistory, moveResult.newState];
          });
          setCurrentMoveIndex(prev => prev + 1);
          setMoveHistory(prev => [...prev, moveNotation]);
          addLog(`Bot move: ${moveNotation}`);
          
          // Update isPlayerTurn - after bot move, it's player's turn
          const isNowPlayerTurn = (config.playerColor === 'white' && moveResult.newState.sideToMove === 'white') ||
                                  (config.playerColor === 'black' && moveResult.newState.sideToMove === 'black');
          isPlayerTurn.current = isNowPlayerTurn;
        }
      } else {
        addLog('Bot could not find a move');
      }
      } catch (error) {
        addLog(`Bot error: ${error}`);
        setStockfishStatus(prev => ({
          ...prev,
          isReady: false,
          lastError: error instanceof Error ? error.message : 'Move request failed'
        }));
        
        // Fallback to native engine when Stockfish fails (if allowed)
        if (config.allowFallback) {
          try {
            addLog('[Fallback] Switching to native bot for this move');
            const fallbackMove = await getBotMove(gameState, config.botElo);
            if (fallbackMove) {
              const moveResult = validateMove(gameState, fallbackMove.from, fallbackMove.to, fallbackMove.promotion);
              if (moveResult.isLegal && moveResult.newState) {
                const moveNotation = `${positionToAlgebraic(fallbackMove.from)}-${positionToAlgebraic(fallbackMove.to)}`;
                setGameState(moveResult.newState);
                setGameStateHistory(prev => {
                  const newHistory = prev.slice(0, currentMoveIndex + 1);
                  return [...newHistory, moveResult.newState];
                });
                setCurrentMoveIndex(prev => prev + 1);
                setMoveHistory(prev => [...prev, moveNotation]);
                addLog(`Bot move (fallback): ${moveNotation}`);

                const isNowPlayerTurn = (config.playerColor === 'white' && moveResult.newState.sideToMove === 'white') ||
                                        (config.playerColor === 'black' && moveResult.newState.sideToMove === 'black');
                isPlayerTurn.current = isNowPlayerTurn;
              }
            }
          } catch (fallbackError) {
            addLog(`Fallback bot error: ${fallbackError}`);
          }
        } else {
          addLog('[Fallback] Disabled - no move made');
          toast.error('Stockfish failed and fallback is disabled');
        }
    } finally {
      setIsThinking(false);
    }
  };

  const newGame = () => {
    const initialState = createInitialGameState();
    setGameState(initialState);
    setGameStateHistory([initialState]);
    setCurrentMoveIndex(0);
    setSelectedSquare(null);
    setHighlightedSquares([]);
    setMoveHistory([]);
    setIsThinking(false);
    setIsGameResigned(false);
    setIsDrawOffered(false);
    setClockResetTrigger(prev => prev + 1); // Reset the clocks
    isPlayerTurn.current = config.playerColor === 'white';
    
    addLog('--- New game started - clocks reset ---');
    
    // If player is black, request bot move immediately
    if (config.playerColor === 'black') {
      setTimeout(requestBotMove, 500);
    }
  };

  const undoLastMove = () => {
    if (currentMoveIndex > 0) {
      const newIndex = currentMoveIndex - 1;
      const previousState = gameStateHistory[newIndex];
      
      setGameState(previousState);
      setCurrentMoveIndex(newIndex);
      setMoveHistory(prev => prev.slice(0, -1));
      setSelectedSquare(null);
      setHighlightedSquares([]);
      
      // Update player turn ref
      const isNowPlayerTurn = (config.playerColor === 'white' && previousState.sideToMove === 'white') ||
                              (config.playerColor === 'black' && previousState.sideToMove === 'black');
      isPlayerTurn.current = isNowPlayerTurn;
      
      addLog('Move undone');
      toast.success('Move undone');
    }
  };

  const undoTwoMoves = () => {
    // Undo both player and bot moves to get back to player's turn
    if (currentMoveIndex >= 2) {
      const newIndex = currentMoveIndex - 2;
      const previousState = gameStateHistory[newIndex];
      
      setGameState(previousState);
      setCurrentMoveIndex(newIndex);
      setMoveHistory(prev => prev.slice(0, -2));
      setSelectedSquare(null);
      setHighlightedSquares([]);
      
      // Update player turn ref
      const isNowPlayerTurn = (config.playerColor === 'white' && previousState.sideToMove === 'white') ||
                              (config.playerColor === 'black' && previousState.sideToMove === 'black');
      isPlayerTurn.current = isNowPlayerTurn;
      
      addLog('Two moves undone');
      toast.success('Two moves undone');
    }
  };

  // Move navigation functions
  const goToFirstMove = () => {
    if (currentMoveIndex > 0) {
      setGameState(gameStateHistory[0]);
      setCurrentMoveIndex(0);
      setSelectedSquare(null);
      setHighlightedSquares([]);
      isPlayerTurn.current = config.playerColor === 'white';
      addLog('Returned to start position');
    }
  };

  const goToPreviousMove = () => {
    if (currentMoveIndex > 0) {
      const newIndex = currentMoveIndex - 1;
      setGameState(gameStateHistory[newIndex]);
      setCurrentMoveIndex(newIndex);
      setSelectedSquare(null);
      setHighlightedSquares([]);
      
      const isNowPlayerTurn = (config.playerColor === 'white' && gameStateHistory[newIndex].sideToMove === 'white') ||
                              (config.playerColor === 'black' && gameStateHistory[newIndex].sideToMove === 'black');
      isPlayerTurn.current = isNowPlayerTurn;
    }
  };

  const goToNextMove = () => {
    if (currentMoveIndex < gameStateHistory.length - 1) {
      const newIndex = currentMoveIndex + 1;
      setGameState(gameStateHistory[newIndex]);
      setCurrentMoveIndex(newIndex);
      setSelectedSquare(null);
      setHighlightedSquares([]);
      
      const isNowPlayerTurn = (config.playerColor === 'white' && gameStateHistory[newIndex].sideToMove === 'white') ||
                              (config.playerColor === 'black' && gameStateHistory[newIndex].sideToMove === 'black');
      isPlayerTurn.current = isNowPlayerTurn;
    }
  };

  const goToLastMove = () => {
    const lastIndex = gameStateHistory.length - 1;
    if (currentMoveIndex < lastIndex) {
      setGameState(gameStateHistory[lastIndex]);
      setCurrentMoveIndex(lastIndex);
      setSelectedSquare(null);
      setHighlightedSquares([]);
      
      const isNowPlayerTurn = (config.playerColor === 'white' && gameStateHistory[lastIndex].sideToMove === 'white') ||
                              (config.playerColor === 'black' && gameStateHistory[lastIndex].sideToMove === 'black');
      isPlayerTurn.current = isNowPlayerTurn;
      addLog('Returned to current position');
    }
  };

  // Game control functions
  const resignGame = () => {
    setIsGameResigned(true);
    const winner = config.playerColor === 'white' ? 'Black' : 'White';
    setGameStatus(`${winner} wins by resignation`);
    addLog(`${config.playerColor === 'white' ? 'White' : 'Black'} resigned - ${winner} wins`);
    toast.info(`You resigned. ${winner} wins.`);
  };

  const offerDraw = () => {
    setIsDrawOffered(true);
    addLog('Draw offered');
    toast.info('Draw offer sent');
    // Auto-accept draw for bot (could be enhanced with bot decision logic)
    setTimeout(() => {
      setGameStatus('Draw by agreement');
      addLog('Draw accepted');
      toast.success('Draw accepted');
    }, 1000);
  };

  const resetSettings = () => {
    setConfig({
      botElo: 800,
      playerColor: 'white',
      timeControl: '10+0',
      engine: 'stockfish',
      allowFallback: true
    });
    toast.success('Settings reset to default');
  };

  // Auto-play bot moves
  useEffect(() => {
    const status = updateGameStatus();
    const isViewingCurrentPosition = currentMoveIndex === gameStateHistory.length - 1;
    
    addLog(`[Auto-move Check] Position: ${currentMoveIndex}/${gameStateHistory.length - 1}, Status: ${status.status}, Side: ${gameState.sideToMove}, Player: ${config.playerColor}, Thinking: ${isThinking}, ViewingCurrent: ${isViewingCurrentPosition}`);
    
    if (status.status === 'ongoing' || status.status === 'check') {
      const shouldBotMove = (config.playerColor === 'white' && gameState.sideToMove === 'black') ||
                           (config.playerColor === 'black' && gameState.sideToMove === 'white');
      
      addLog(`[Auto-move Check] ShouldBotMove: ${shouldBotMove}, All conditions: ${shouldBotMove && !isThinking && isViewingCurrentPosition}`);
      
      if (shouldBotMove && !isThinking && isViewingCurrentPosition) {
        addLog('[Auto-move] Triggering bot move in 500ms');
        const timer = setTimeout(() => {
          requestBotMove();
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [gameState, config.playerColor, isThinking, currentMoveIndex, gameStateHistory.length]);
  
  // Persist settings
  useEffect(() => {
    try {
      localStorage.setItem('engine:config', JSON.stringify(config));
    } catch {}
  }, [config]);

  // Load settings
  useEffect(() => {
    try {
      const raw = localStorage.getItem('engine:config');
      if (raw) {
        const parsed = JSON.parse(raw);
          if (parsed && (parsed.engine === 'native' || parsed.engine === 'stockfish')) {
            setConfig(prev => ({
              botElo: typeof parsed.botElo === 'number' ? parsed.botElo : prev.botElo,
              playerColor: parsed.playerColor === 'black' ? 'black' : 'white',
              timeControl: typeof parsed.timeControl === 'string' ? parsed.timeControl : prev.timeControl,
              engine: parsed.engine,
              allowFallback: typeof parsed.allowFallback === 'boolean' ? parsed.allowFallback : prev.allowFallback
            }));
          }
      }
    } catch {}
  }, []);
  
  // Check for time flags
  useEffect(() => {
    if (isTimeUp) {
      if (whiteTimeMs <= 0) {
        addLog('White loses on time');
        setGameStatus('Black wins on time');
      } else if (blackTimeMs <= 0) {
        addLog('Black loses on time');  
        setGameStatus('White wins on time');
      }
    }
  }, [isTimeUp, whiteTimeMs, blackTimeMs]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) {
        return; // Don't trigger shortcuts when typing in inputs
      }
      
      if (e.key === ' ') {
        e.preventDefault();
        setIsFlipped(!isFlipped);
      } else if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        undoTwoMoves();
      } else if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        newGame();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPreviousMove();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextMove();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToFirstMove();
      } else if (e.key === 'End') {
        e.preventDefault();
        goToLastMove();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFlipped, currentMoveIndex, gameStateHistory.length]);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Badge 
            variant="outline" 
            className="bg-gradient-to-r from-teen-blue/20 to-teen-purple/20 border-teen-blue/50 text-teen-blue"
          >
            {config.engine === 'stockfish' ? '🧠 Super Brain AI' : '🎯 Smart Bot'}
          </Badge>
          {config.engine === 'stockfish' && (
            <Badge 
              variant={stockfishStatus.isReady ? "default" : "destructive"}
              className={`text-xs ${stockfishStatus.isReady ? 'bg-teen-green/20 text-teen-green border-teen-green/50' : ''}`}
            >
              {stockfishStatus.isReady ? `✅ Ready!` : '⚠️ Loading...'}
            </Badge>
          )}
        </div>
        <h1 className="text-4xl font-bold bg-gradient-to-r from-teen-blue via-teen-purple to-teen-gold bg-clip-text text-transparent mb-2">
          🎮 Chess Battle Arena 🏆
        </h1>
        <p className="text-lg text-muted-foreground">{gameStatus}</p>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Board Section */}
        <Card className="bg-gradient-to-br from-background via-background to-teen-blue/5 border-teen-blue/20 shadow-teen-glow">
          <CardContent className="p-6">
            {/* Chess Clocks */}
            <div className="flex justify-between mb-4 text-sm">
              <div className={`p-3 rounded-lg font-mono transition-all duration-300 ${
                activePlayer === 'black' && currentGameStatus === 'ongoing' 
                  ? 'bg-gradient-to-r from-teen-purple to-teen-blue text-white shadow-teen-purple scale-105' 
                  : 'bg-muted hover:bg-muted/80'
              }`}>
                ⚫ Black: {formatTime(blackTimeMs)}
              </div>
              <div className={`p-3 rounded-lg font-mono transition-all duration-300 ${
                activePlayer === 'white' && currentGameStatus === 'ongoing' 
                  ? 'bg-gradient-to-r from-teen-gold to-teen-green text-white shadow-teen-gold scale-105' 
                  : 'bg-muted hover:bg-muted/80'
              }`}>
                ⚪ White: {formatTime(whiteTimeMs)}
              </div>
            </div>
            
            <div className="flex justify-center mb-4">
              <NativeChessBoard
                gameState={gameState}
                onSquareClick={handleSquareClick}
                highlightedSquares={highlightedSquares}
                selectedSquare={selectedSquare}
                flipped={isFlipped}
              />
            </div>
            
            {/* Game Controls */}
            <div className="space-y-4 mb-4">
              {/* Main Controls */}
              <div className="flex flex-wrap gap-3 justify-center">
                <Button 
                  onClick={newGame} 
                  className="bg-gradient-to-r from-teen-green to-teen-blue hover:from-teen-green/80 hover:to-teen-blue/80 text-white shadow-teen-green hover:scale-105 transition-all duration-200" 
                  size="sm"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  🎮 New Battle
                </Button>
                <Button 
                  onClick={undoTwoMoves} 
                  variant="outline" 
                  size="sm" 
                  disabled={currentMoveIndex < 2 || isGameResigned || isDrawOffered}
                  title="Undo last two moves (Ctrl+Z)"
                  className="border-teen-purple/50 text-teen-purple hover:bg-teen-purple/10 hover:scale-105 transition-all duration-200"
                >
                  <RotateCw className="mr-2 h-4 w-4" />
                  ↩️ Undo Turn
                </Button>
                <Button 
                  onClick={() => setIsFlipped(!isFlipped)} 
                  variant="outline" 
                  size="sm" 
                  title="Flip board (Space)"
                  className="border-teen-gold/50 text-teen-gold hover:bg-teen-gold/10 hover:scale-105 transition-all duration-200"
                >
                  <FlipHorizontal className="mr-2 h-4 w-4" />
                  🔄 Flip Board
                </Button>
              </div>

               {/* Move Navigation */}
               <div className="flex gap-2 justify-center bg-muted/30 p-2 rounded-lg">
                 <Button 
                   onClick={goToFirstMove} 
                   variant="ghost" 
                   size="sm" 
                   disabled={currentMoveIndex === 0}
                   title="Go to start (Home)"
                   className="hover:bg-teen-blue/20 hover:text-teen-blue transition-all duration-200 disabled:opacity-30"
                 >
                   ⏮️
                 </Button>
                 <Button 
                   onClick={goToPreviousMove} 
                   variant="ghost" 
                   size="sm" 
                   disabled={currentMoveIndex === 0}
                   title="Previous move (←)"
                   className="hover:bg-teen-blue/20 hover:text-teen-blue transition-all duration-200 disabled:opacity-30"
                 >
                   ⬅️
                 </Button>
                 <Button 
                   onClick={goToNextMove} 
                   variant="ghost" 
                   size="sm" 
                   disabled={currentMoveIndex >= gameStateHistory.length - 1}
                   title="Next move (→)"
                   className="hover:bg-teen-blue/20 hover:text-teen-blue transition-all duration-200 disabled:opacity-30"
                 >
                   ➡️
                 </Button>
                 <Button 
                   onClick={goToLastMove} 
                   variant="ghost" 
                   size="sm" 
                   disabled={currentMoveIndex >= gameStateHistory.length - 1}
                   title="Go to end (End)"
                   className="hover:bg-teen-blue/20 hover:text-teen-blue transition-all duration-200 disabled:opacity-30"
                 >
                   ⏭️
                 </Button>
               </div>

               {/* Game Actions */}
               <div className="flex flex-wrap gap-2 justify-center">
                 <Button 
                   onClick={resignGame} 
                   variant="outline" 
                   size="sm" 
                   disabled={isGameResigned || isDrawOffered || moveHistory.length === 0}
                   className="text-red-400 border-red-400/50 hover:bg-red-400/10 hover:scale-105 transition-all duration-200"
                 >
                   <Flag className="mr-2 h-4 w-4" />
                   😔 Give Up
                 </Button>
                 <Button 
                   onClick={offerDraw} 
                   variant="outline" 
                   size="sm" 
                   disabled={isGameResigned || isDrawOffered || moveHistory.length < 2}
                   className="text-yellow-400 border-yellow-400/50 hover:bg-yellow-400/10 hover:scale-105 transition-all duration-200"
                 >
                   <Handshake className="mr-2 h-4 w-4" />
                   🤝 Call Draw
                 </Button>
                 <Button 
                   onClick={requestBotMove} 
                   variant="ghost" 
                   size="sm" 
                   disabled={isThinking || (gameState.sideToMove === config.playerColor)}
                   title="Force AI to move"
                   className="text-teen-purple hover:bg-teen-purple/20 hover:scale-105 transition-all duration-200"
                 >
                   <Brain className="mr-2 h-4 w-4" />
                   🧠 Make AI Move
                 </Button>
                  <Button 
                    onClick={resetSettings} 
                    variant="ghost" 
                    size="sm" 
                    title="Reset all settings"
                    className="text-muted-foreground hover:bg-muted/50 hover:scale-105 transition-all duration-200"
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    ⚙️ Reset
                  </Button>
                  {config.engine === 'stockfish' && (
                    <Button 
                      onClick={runSanityTest} 
                      variant="ghost" 
                      size="sm" 
                      disabled={isTesting}
                      title="Test Super Brain AI"
                      className="text-teen-green hover:bg-teen-green/20 hover:scale-105 transition-all duration-200"
                    >
                      {isTesting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="mr-2 h-4 w-4" />
                      )}
                      🧪 Test AI
                    </Button>
                  )}
               </div>

              {/* Status Indicators */}
              {isThinking && (
                <div className="flex items-center justify-center bg-gradient-to-r from-teen-purple/20 to-teen-blue/20 p-3 rounded-lg border border-teen-purple/30">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-teen-purple" />
                  <span className="text-teen-purple font-medium">🤔 AI is thinking super hard...</span>
                </div>
              )}
              
              {currentMoveIndex < gameStateHistory.length - 1 && (
                <div className="text-center bg-teen-gold/10 p-2 rounded-lg border border-teen-gold/30">
                  <span className="text-teen-gold font-medium">
                    📽️ Replaying: Move {currentMoveIndex} of {gameStateHistory.length - 1}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-4 text-sm">
              <div>
                <Label className="text-sm font-medium text-teen-purple">📜 Battle History</Label>
                <div className="font-mono text-xs bg-gradient-to-r from-muted/50 to-teen-blue/5 p-3 rounded-lg border border-teen-blue/20 max-h-20 overflow-y-auto">
                  {moveHistory.length > 0 ? (
                    <span className="text-foreground">{moveHistory.join(' • ')}</span>
                  ) : (
                    <span className="text-muted-foreground italic">🎯 Ready for your first move!</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Controls & Analysis Section */}
        <div className="space-y-6">
          {/* Bot Settings */}
          <Card className="bg-gradient-to-br from-background via-background to-teen-purple/5 border-teen-purple/20 shadow-teen-glow">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl bg-gradient-to-r from-teen-purple to-teen-blue bg-clip-text text-transparent">
                ⚙️ Game Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-teen-blue/5 p-4 rounded-lg border border-teen-blue/20">
                <Label htmlFor="bot-elo" className="text-teen-blue font-medium">🎯 Pick Your Challenge Level</Label>
                {config.engine === 'stockfish' ? (
                  <div className="space-y-3 pt-3">
                    <Slider
                      min={STOCKFISH_UCI_ELO_MIN}
                      max={STOCKFISH_UCI_ELO_MAX}
                      step={10}
                      value={[config.botElo]}
                      onValueChange={(value) => setConfig(prev => ({ ...prev, botElo: value[0] }))}
                      className="[&>span:first-child]:bg-gradient-to-r [&>span:first-child]:from-teen-blue [&>span:first-child]:to-teen-purple"
                    />
                    <div className="flex justify-between text-xs">
                      <span className="text-teen-green">😊 Beginner</span>
                      <span className="text-teen-gold font-mono">Skill: {clampStockfishElo(config.botElo)}</span>
                      <span className="text-teen-purple">🔥 Master</span>
                    </div>
                  </div>
                ) : (
                  <Select 
                    value={config.botElo.toString()} 
                    onValueChange={(value) => setConfig(prev => ({ ...prev, botElo: parseInt(value) }))}
                  >
                    <SelectTrigger className="mt-2 border-teen-blue/30 focus:border-teen-blue">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableBotElos().map(elo => {
                        const personality = getBotPersonality(elo);
                        return (
                          <SelectItem key={elo} value={elo.toString()}>
                            🤖 {personality?.name} ({elo} skill points)
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="bg-teen-purple/5 p-4 rounded-lg border border-teen-purple/20">
                <Label htmlFor="engine" className="text-teen-purple font-medium">🧠 Choose Your AI Opponent</Label>
                <Select 
                  value={config.engine}
                  onValueChange={(value: 'native' | 'stockfish') => setConfig(prev => ({ ...prev, engine: value }))}
                >
                  <SelectTrigger className="mt-2 border-teen-purple/30 focus:border-teen-purple">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="native">🎯 Smart Bot (Perfect for learning!)</SelectItem>
                    <SelectItem value="stockfish">🧠 Super Brain AI (World's strongest!)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="bg-teen-gold/5 p-4 rounded-lg border border-teen-gold/20">
                <Label htmlFor="player-color" className="text-teen-gold font-medium">🎨 Pick Your Team</Label>
                <Select 
                  value={config.playerColor} 
                  onValueChange={(value: 'white' | 'black') => setConfig(prev => ({ ...prev, playerColor: value }))}
                >
                  <SelectTrigger className="mt-2 border-teen-gold/30 focus:border-teen-gold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="white">⚪ Team White (Goes first!)</SelectItem>
                    <SelectItem value="black">⚫ Team Black (Strategic!)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="bg-teen-green/5 p-4 rounded-lg border border-teen-green/20">
                <Label htmlFor="time-control" className="text-teen-green font-medium">⏱️ Game Speed</Label>
                <Select 
                  value={config.timeControl} 
                  onValueChange={(value) => setConfig(prev => ({ ...prev, timeControl: value }))}
                >
                  <SelectTrigger className="mt-2 border-teen-green/30 focus:border-teen-green">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1+0">⚡ Lightning Fast (1 min)</SelectItem>
                    <SelectItem value="3+0">🔥 Quick Battle (3 min)</SelectItem>
                    <SelectItem value="5+0">⚡ Fast Paced (5 min)</SelectItem>
                    <SelectItem value="10+0">🎯 Standard (10 min)</SelectItem>
                    <SelectItem value="15+10">🧠 Thoughtful (15+10)</SelectItem>
                    <SelectItem value="30+0">🏰 Epic Battle (30 min)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {config.engine === 'stockfish' && (
                <div className="bg-muted/30 p-4 rounded-lg border">
                  <Label className="text-sm font-medium">🔧 Advanced Settings</Label>
                  <div className="flex items-center space-x-3 mt-2 text-sm">
                    <input
                      type="checkbox"
                      id="allow-fallback"
                      checked={config.allowFallback}
                      onChange={(e) => setConfig(prev => ({ ...prev, allowFallback: e.target.checked }))}
                      className="rounded border-teen-blue/30 text-teen-blue focus:ring-teen-blue"
                    />
                    <Label htmlFor="allow-fallback" className="text-sm font-normal">
                      🔄 Auto-switch to Smart Bot if Super Brain AI has issues
                    </Label>
                  </div>
                  {stockfishStatus.lastError && (
                    <div className="text-xs text-red-400 bg-red-400/10 p-2 rounded-lg mt-2 border border-red-400/20">
                      ⚠️ Issue: {stockfishStatus.lastError}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Game Analysis */}
          <Card className="bg-gradient-to-br from-background via-background to-teen-green/5 border-teen-green/20 shadow-teen-glow">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl bg-gradient-to-r from-teen-green to-teen-gold bg-clip-text text-transparent">
                📊 Battle Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="flex justify-between items-center p-2 bg-teen-green/5 rounded-lg">
                  <span className="text-teen-green font-medium">🎯 Total Moves:</span>
                  <span className="font-mono text-lg">{Math.floor(gameState.fullmoveNumber)} ({currentMoveIndex}/{gameStateHistory.length - 1})</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-teen-blue/5 rounded-lg">
                  <span className="text-teen-blue font-medium">🎮 Turn:</span>
                  <span className="capitalize text-lg">{gameState.sideToMove === 'white' ? '⚪ White' : '⚫ Black'}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-teen-purple/5 rounded-lg">
                  <span className="text-teen-purple font-medium">📈 Status:</span>
                  <span className="text-sm font-medium">{gameStatus}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engine Log */}
          <Card className="bg-gradient-to-br from-background via-background to-teen-gold/5 border-teen-gold/20 shadow-teen-glow">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl bg-gradient-to-r from-teen-gold to-teen-green bg-clip-text text-transparent">
                📚 Game Story
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                ref={logRef}
                className="bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 p-4 rounded-lg text-xs font-mono h-32 overflow-auto border border-teen-gold/20 shadow-inner"
              >
                {logs.map((log, index) => (
                  <div key={index} className="py-0.5 hover:bg-slate-700/30 px-1 rounded">
                    {log.includes('✓') ? '🎉 ' : log.includes('✗') ? '❌ ' : log.includes('thinking') ? '🤔 ' : '📝 '}
                    {log}
                  </div>
                ))}
                {logs.length === 0 && (
                  <div className="text-slate-400 italic text-center py-4">
                    🎯 Your chess adventure will be logged here...
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
